import { createAsyncThunk } from "@reduxjs/toolkit";

import axios from "../../axios/signupAxios";
import ax from "../../axios/defaultAxios";
import { initialState } from "../slices/auth-slice";
import { slices } from "../../constants/sliceNames";
import { errorMessage, successMessage } from "../actions/messages.actions";

/******* Sign up ********/

export const requestSignUp = createAsyncThunk(
  `${slices.auth}/requestSignUp`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post("/signup", data);
      dispatch(successMessage(response.data.message));
      return response.data;
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestSignUpStatus = {
  [requestSignUp.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestSignUp.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSignUp.rejected]: (state, action) => {
    state.loading = false;
    state.errorMsg = action.payload.error;
  },
};

/******* Sign in ********/

export const requestSignIn = createAsyncThunk(
  `${slices.auth}/requestSignIn`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post("/login", data);
      dispatch(successMessage(response.data.message));
      return response.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.response?.data?.error || error.message));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestSignInStatus = {
  [requestSignIn.fulfilled]: (state, action) => {
    state.userToken = action.payload.token;
    state.loading = false;
  },
  [requestSignIn.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSignIn.rejected]: (state, action) => {
    state.errorMsg = action.payload.errorMsg;
    state.loading = false;
  },
};

/******* Sign out ********/

export const requestSignOut = createAsyncThunk(
  `${slices.auth}/requestSignOut`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      dispatch(successMessage("You have been signed out successfully."));
      return true;
    } catch (error) {
      dispatch(errorMessage(error.response.data.message, "Error signing out!"));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestSignOutStatus = {
  [requestSignOut.fulfilled]: (state, action) => {
    return {
      ...initialState,
    };
  },
  [requestSignOut.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSignOut.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* social auth ********/

export const requestSocialAuth = createAsyncThunk(
  `${slices.auth}/requestSocialAuth`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/auth/${data.source}`, data);
      return {
        data: response.data,
        isGithub: data.source === "github",
      };
    } catch (error) {
      dispatch(errorMessage(error.message));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestSocialAuthStatus = {
  [requestSocialAuth.fulfilled]: (state, action) => {
    state.userToken = action.payload.data.token;
    state.userName = action.payload.data.username;
    state.remember = true;
    state.loading = false;
    state.isGithub = action.payload.isGithub;
  },
  [requestSocialAuth.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSocialAuth.rejected]: (state, action) => {
    state.loading = false;
    state.errorMsg = action.payload.errorMsg;
  },
};

/******* Github ********/

export const requestGithubLink = createAsyncThunk(
  `${slices.auth}/requestGithubLink`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`/users/auth-github?code=${data}`);
      return { data: response.data };
    } catch (error) {
      dispatch(errorMessage(error.message));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestGithubLinkStatus = {
  [requestGithubLink.fulfilled]: (state, action) => {
    state.isGithub = true;
  },
  [requestGithubLink.pending]: (state, action) => {},
  [requestGithubLink.rejected]: (state, action) => {},
};

export const requestUserInfo = createAsyncThunk(
  `${slices.auth}/requestUserInfo`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await ax.get(`/users/v2/info`);
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error));
      if (error.response?.status === 401 || error.response?.status === 400) {
        dispatch(requestSignOut());
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestUserInfoStatus = {
  [requestUserInfo.fulfilled]: (state, action) => {
    const data = action.payload;
    state.fetchDone = true;
    state.userInfo = data;
    state.userName = data.username;
    state.userID = data.user_id;
    state.userRole = data.roles;
    state.organizations = data.organizations.sort();
  },
  [requestUserInfo.pending]: (state, action) => {
    state.fetchDone = false;
  },
  [requestUserInfo.rejected]: (state, action) => {},
};
// ----------------------------------------------------------------------

/******* Recover password ********/

export const requestRecoverPassword = createAsyncThunk(
  `${slices.auth}/requestRecoverPassword`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/forgetPassword`, { email: data });
      if (response.status === 200 || response.status === 201) {
        console.log("response", response);
        return response.data;
      }

      throw new Error(response.data.data.message);
    } catch (error) {
      dispatch(errorMessage(error.response.data.message || error.message));
      return rejectWithValue(error);
    }
  }
);

export const requestRecoverPasswordStatus = {
  [requestRecoverPassword.fulfilled]: (state, action) => {
    state.recoverStatus = true;
  },
  [requestRecoverPassword.pending]: (state, action) => {},
  [requestRecoverPassword.rejected]: (state, action) => {
    state.recoverStatus = false;
    state.recoverErrorMsg = action.payload.errorMsg;
  },
};

/******* Activate Account ********/

export const requestActivateAccount = createAsyncThunk(
  `${slices.auth}/requestActivateAccount`,
  async (data, { rejectWithValue, dispatch }) => {
    const { email, token } = data;
    try {
      const response = await axios.post(`/activate/${email}/${token}`);
      if (response.status === 200 || response.status === 201) {
        const token = response.data.userToken;
        return { token };
      }
      throw new Error(response.data.data.message);
    } catch (error) {
      dispatch(errorMessage(error.response.data.message || error.message));
      return rejectWithValue({
        errorMsg: error.response.data.error,
      });
    }
  }
);

export const requestActivateAccountStatus = {
  [requestActivateAccount.fulfilled]: (state, action) => {
    state.userToken = action.payload.token;
  },
  [requestActivateAccount.pending]: (state, action) => {},
  [requestActivateAccount.rejected]: (state, action) => {
    state.errorMsg = action.payload.errorMsg;
  },
};

/******* Resend Activation ********/

export const requestResendActivation = createAsyncThunk(
  `${slices.auth}/requestResendActivation`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/resendactivation`, { data });
      if (response.status === 200 || response.status === 201) return response.data;

      throw new Error(response.data.data.message);
    } catch (error) {
      dispatch(errorMessage(error.response.data.message || error.message));
      return rejectWithValue(error);
    }
  }
);

export const requestResendActivationStatus = {
  [requestResendActivation.fulfilled]: (state, action) => {
    state.resendActivationStatus = true;
    state.loading = false;
  },
  [requestResendActivation.pending]: (state, action) => {
    state.loading = true;
  },
  [requestResendActivation.rejected]: (state, action) => {
    state.resendActivationStatus = false;
    state.errorMsg = action.payload.errorMsg;
    state.loading = false;
  },
};

/// reset actions doesn't affects the state
export const resetPassword = (data) => {
  return (dispatch) => {
    return ax.post(`/resetPassword`, data);
  };
};

export const authorizeResetToken = (token) => {
  return (dispatch) => {
    return ax.get(`/forgetPassword/validate/${token}`);
  };
};

/***** Validate Invitation *****/
export const validateInvitation = createAsyncThunk(
  `${slices.auth}/validateInvitation`,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`/invitation/validate/${id}`);
      if (response.status === 200 || response.status === 201) {
        return response.data;
      }
    } catch (error) {
      dispatch(errorMessage(error.response.data.error || error.message));
      return rejectWithValue(error);
    }
  }
);

export const validateInvitationStatus = {
  [validateInvitation.fulfilled]: (state, action) => {
    state.validating = false;
    state.invitationValid = true;
  },
  [validateInvitation.pending]: (state, action) => {
    state.validating = true;
  },
  [validateInvitation.rejected]: (state, action) => {
    state.validating = false;
    state.invitationValid = false;
  },
};

export const validateResetPasswordToken = createAsyncThunk(
  `${slices.auth}/validateResetPasswordToken`,
  async (token, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`/forgetPassword/validate/${token}`);
      return response.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const resetPasswordData = createAsyncThunk(
  `${slices.auth}/resetPasswordData`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/resetPassword`, data);
      dispatch(successMessage(response.data.message));
      return response.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
